class HBSearch {
  constructor(root) {
    this.root = root;
    this.input = root.querySelector('[data-hb-search-input]');
    this.panel = root.querySelector('.hb-search-panel');
    this.initial = root.querySelector('[data-hb-search-initial]');
    this.results = root.querySelector('[data-hb-search-results]');
    this.status = root.querySelector('[data-hb-search-status]');
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.input.addEventListener('focus', () => this.open(), options);
    this.input.addEventListener('input', event => {
      if (!event.isComposing) this.schedule();
    }, options);
    this.input.addEventListener('compositionend', () => this.schedule(), options);
    root.addEventListener('keydown', event => this.keydown(event), options);
    root.querySelector('[data-hb-search-close]').addEventListener('click', () => this.close(true), options);
    root.querySelector('form').addEventListener('submit', event => {
      if (!this.input.value.trim()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.input.focus();
      }
    }, { ...options, capture: true });
    this.toggle = root.closest('[data-site-header]')?.querySelector('[data-hb-search-toggle]');
    this.toggle?.setAttribute('aria-controls', this.panel.id);
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.toggle?.addEventListener('click', () => {
      this.open();
      this.input.focus();
    }, options);
    document.addEventListener('click', event => {
      if (!root.contains(event.target) && !this.toggle?.contains(event.target)) this.close();
    }, options);
    document.addEventListener('focusin', event => {
      if (!root.contains(event.target) && !this.toggle?.contains(event.target)) this.close();
    }, options);
    root.hbSearch = this;
  }

  open() {
    if (!this.panel.hidden) return;
    this.panel.hidden = false;
    this.root.classList.add('hb-search--open');
    this.input.setAttribute('aria-expanded', 'true');
    this.toggle?.setAttribute('aria-expanded', 'true');
    this.schedule();
  }

  cancel() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.version = (this.version || 0) + 1;
    this.results.removeAttribute('aria-busy');
  }

  close(restoreFocus = false) {
    // Focus first so the focus handler cannot reopen a closed panel.
    if (restoreFocus) {
      if (matchMedia('(max-width: 719px)').matches && this.toggle) this.toggle.focus();
      else this.input.focus();
    }
    this.cancel();
    this.panel.hidden = true;
    this.root.classList.remove('hb-search--open');
    this.input.setAttribute('aria-expanded', 'false');
    this.toggle?.setAttribute('aria-expanded', 'false');
  }

  schedule() {
    this.cancel();
    const term = this.input.value.trim();
    this.results.hidden = true;
    this.initial.hidden = Boolean(term);
    this.status.textContent = term ? this.root.dataset.loading : '';
    if (!term) return;
    this.panel.hidden = false;
    this.root.classList.add('hb-search--open');
    this.input.setAttribute('aria-expanded', 'true');
    const version = this.version;
    this.timer = setTimeout(() => this.search(term, version), 200);
  }

  endpoint(term) {
    const url = new URL(this.root.dataset.suggestUrl, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'hb-predictive-search');
    url.searchParams.set('resources[type]', 'query,collection,product,page,article');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    return url;
  }

  async search(term, version) {
    this.request = new AbortController();
    this.results.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(this.endpoint(term), { signal: this.request.signal });
      if (!response.ok) throw new Error(`Search: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-hb-predictive-content]');
      if (!content) throw new Error('Missing predictive search content');
      if (version !== this.version || this.panel.hidden) return;
      this.results.dispatchEvent(new CustomEvent('theme:rimg:unwatch', { bubbles: true }));
      this.results.replaceChildren(content);
      this.results.hidden = false;
      this.results.dispatchEvent(new CustomEvent('theme:rimg:watch', { bubbles: true }));
      this.status.textContent = this.root.dataset.loaded;
    } catch (error) {
      if (error.name === 'AbortError' || version !== this.version || this.panel.hidden) return;
      this.status.textContent = this.root.dataset.error;
      this.results.replaceChildren();
    } finally {
      if (version === this.version) this.results.removeAttribute('aria-busy');
    }
  }

  keydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (this.panel.hidden) this.open();
    const content = this.input.value.trim() ? this.results : this.initial;
    const links = [...content.querySelectorAll('a[href], button')];
    if (content.hidden || !links.length) return;
    event.preventDefault();
    const index = links.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? index + 1 : (index < 0 ? links.length - 1 : index - 1);
    if (next < 0 || next >= links.length) this.input.focus();
    else links[next].focus();
  }

  destroy() {
    this.cancel();
    this.events.abort();
    delete this.root.hbSearch;
  }
}

function initHBSearch(container = document) {
  container.querySelectorAll('[data-hb-search]').forEach(root => {
    if (!root.hbSearch) new HBSearch(root);
  });
}
initHBSearch();
document.addEventListener('shopify:section:load', event => initHBSearch(event.target));
document.addEventListener('shopify:section:unload', event => {
  event.target.querySelectorAll('[data-hb-search]').forEach(root => root.hbSearch?.destroy());
});
